import React from "react";
import { Bot, Battery, Zap, Cpu, Lock } from "lucide-react";

const cormorant = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

/**
 * Autonomous Operative reserved-slot grid.
 * Cosmetic only — no real dispatch. Mounted on the Field Operative
 * dashboard to visually carry the Director's "Code today. Steel tomorrow."
 * roadmap until the Robotic Dispatch bridge is wired.
 *
 * See /app/memory/ROBOTIC_DISPATCH_ROADMAP.md for the full architecture.
 */
const SLOTS = [
    {
        id: "auto-01",
        codename: "STEEL-01",
        kind: "Quadruped patrol",
        capability: "Vault perimeter · TechOpsSA",
        accent: "hsl(195 70% 65%)",
    },
    {
        id: "auto-02",
        codename: "AERIAL-02",
        kind: "Aerial inspection",
        capability: "Property Foundry · Real Estate",
        accent: "hsl(280 60% 70%)",
    },
    {
        id: "auto-03",
        codename: "AMR-03",
        kind: "Warehouse AMR",
        capability: "Artisan Gold Platform · Provisioning",
        accent: "hsl(45 85% 68%)",
    },
];

export default function AutonomousSlots() {
    return (
        <section
            data-testid="autonomous-slots"
            className="relative my-10"
        >
            <div className="flex items-center gap-3 mb-5">
                <div className="text-[10px] uppercase tracking-[0.45em] text-[hsl(195_70%_65%)] font-mono flex items-center gap-2">
                    <Bot size={11} /> Autonomous Operatives · Reserved Slots
                </div>
                <div className="h-px flex-1 bg-[hsl(40_40%_25%/0.4)]" />
                <span className="text-[9px] uppercase tracking-[0.4em] text-muted-foreground/55 font-mono">
                    PHASE 1 · ROADMAP
                </span>
            </div>

            <p
                className="mb-5 text-sm text-muted-foreground italic max-w-2xl"
                style={cormorant}
            >
                "Code today. Steel tomorrow. Sovereignty forever."
            </p>

            <div className="grid sm:grid-cols-3 gap-3">
                {SLOTS.map((s) => (
                    <article
                        key={s.id}
                        data-testid={`auto-slot-${s.id}`}
                        className="relative rounded-xl p-4 border border-dashed border-[hsl(40_40%_25%/0.4)] bg-[hsl(151_55%_5%/0.5)] backdrop-blur-sm group hover:border-[var(--auto-accent)] transition-colors"
                        style={{ "--auto-accent": s.accent }}
                    >
                        {/* Reserved overlay */}
                        <span
                            aria-hidden="true"
                            className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[8px] uppercase tracking-[0.35em] font-mono"
                            style={{
                                background: "rgba(0,0,0,0.5)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                color: "hsl(40 25% 55%)",
                            }}
                        >
                            <Lock size={8} /> Reserved
                        </span>

                        <div className="flex items-center gap-2.5">
                            <span
                                className="h-9 w-9 rounded-full flex items-center justify-center"
                                style={{
                                    background: "rgba(0,0,0,0.5)",
                                    border: `1px solid ${s.accent}55`,
                                }}
                            >
                                <Cpu size={14} style={{ color: s.accent }} />
                            </span>
                            <div>
                                <div
                                    className="text-[10px] uppercase tracking-[0.4em] font-mono"
                                    style={{ color: s.accent }}
                                >
                                    {s.codename}
                                </div>
                                <div className="text-sm text-foreground/80 font-medium">
                                    {s.kind}
                                </div>
                            </div>
                        </div>

                        <p className="mt-3 text-xs text-muted-foreground">{s.capability}</p>

                        {/* Faux-telemetry strip — visually consistent with future live data */}
                        <div className="mt-4 pt-3 border-t border-[hsl(40_40%_25%/0.25)] flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-muted-foreground/60 font-mono">
                            <span className="flex items-center gap-1">
                                <Battery size={10} /> ──
                            </span>
                            <span className="flex items-center gap-1">
                                <Zap size={10} /> stand-by
                            </span>
                        </div>
                    </article>
                ))}
            </div>

            <div className="mt-4 text-[10px] text-muted-foreground/60 font-mono leading-relaxed">
                Autonomous Operative slots are <strong className="text-[hsl(45_85%_68%)]">cosmetic placeholders</strong> until the
                <em> eigoo-fleet-bridge</em> microservice ships. Web-tier never issues kinetic commands. See
                <span className="font-mono"> /app/memory/ROBOTIC_DISPATCH_ROADMAP.md</span>.
            </div>
        </section>
    );
}
